import React, { useEffect, useState, useMemo } from 'react';
import { Typography, Box, Paper, Stack, Divider, FormControlLabel, Switch } from '@mui/material';
import { equalShallowCheck } from '../../utils/formUtils'
import { formatDate } from '../../utils/dateFormatter'
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

const defaultAudit = {
  createdBy: 0,
  createdByName: '',
  createdOn: '',
  modifiedBy: 0,
  modifiedByName: '',
  modifiedOn: ''
};

function UserForm({
  mode ='create',
  initialValues = null,
  types = [],
  availableUserGroups=[],
  onSubmit,
  onDirtyChange,
  onChangePasswordClick,
  isAdmin = true,
  isEditingOwnProfile = false
}){

  const isEditMode = mode === 'edit';
  
  const [formData, setFormData] = useState({
    id: 0,
    username: '',
    x3fullname: '',
    name:'',
    email:'',
    password:'',
    type:'',
    isActive: true,
    groupIds: [],
    audit: { ...defaultAudit }
  });

  const [errors, setErrors] = useState({});
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  const normaliseGroupIds = (rawGroupIds, groupsLookup) => {
    const raw = Array.isArray(rawGroupIds) ? rawGroupIds : [];

    if (!groupsLookup?.length) return raw; // can't map yet; keep what we have

    const byId = new Set(groupsLookup.map((g) => String(g.id)));
    const byNameToId = new Map(groupsLookup.map((g) => [String(g.name), String(g.id)]));

    return raw
      .map((x) => String(x))
      .map((x) => (byId.has(x) ? x : byNameToId.get(x))) // if it's a name, map to id
      .filter(Boolean);
  };

  const buildModelFromInitial = (init, groupsLookup) => {
    const audit = init?.audit || {};
    return {
      id: init?.id || init?._id || 0,
      username: init?.username || "",
      x3fullname: init?.x3fullname || "",
      name: init?.name || "",
      email: init?.email || "",
      password: "", // never prefill passwords in edit mode
      type: (init?.type || "").toLowerCase(),
      isActive: !!init?.isActive,
      groupIds: normaliseGroupIds(init?.groupIds, groupsLookup),
      audit: {
        createdBy: audit?.createdBy ?? 0,
        createdByName: audit?.createdByName ?? "",
        createdOn: audit?.createdOn ?? "",
        modifiedBy: audit?.modifiedBy ?? 0,
        modifiedByName: audit?.modifiedByName ?? "",
        modifiedOn: audit?.modifiedOn ?? "",
      },
    };
  };

  useEffect(() => {
    console.log('initialValues', initialValues);
    if (!initialValues) return;

    const model = buildModelFromInitial(initialValues, availableUserGroups);
    setFormData(model);
    setInitialSnapshot(model);
    setErrors({});

  }, [initialValues, availableUserGroups]);

  // useEffect(() => {
  //   if (initialSnapshot) return;

  //   const dirty = equalShallowCheck(initialSnapshot, formData);

  //   setIsDirty(dirty);
  //   if (typeof onDirtyChange === 'function') onDirtyChange(dirty);
  // }, [formData, initialSnapshot, onDirtyChange]);

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;

      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    };

    const handleGroupsChange = (_, selectedOptions) => {
      const safeOptions = selectedOptions || [];
      const ids = safeOptions.map(opt => opt.id);
       setFormData((prev) => ({
        ...prev,
        groupIds: ids,
      }));
    };

    const validate = () => {
      let newErrors = {};

      if (!formData.username || !formData.username.trim()) { newErrors.username = 'Username is required'; }
      if (!formData.email || !formData.email.trim()) { newErrors.email = 'Email is required'; }
      if (!formData.type) { newErrors.type = 'Type is required'; }
      // Only required on create
      if (!isEditMode && !formData.password.trim()) {
        newErrors.password = "Password is required.";
      } else if (!isEditMode && formData.password.trim().length < 12) {
        newErrors.password = "Password must be at least 12 characters.";
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleSubmitInternal = (e) => {
      e.preventDefault();
      if (!validate()) return;
      onSubmit?.(formData);
    };



  useEffect(() => {
    if (!isEditMode) return;
    if (!initialValues) return;
    if (!availableUserGroups || !availableUserGroups.length === 0) return;

    const userGroupNames = initialValues.groupIds || [];

    setFormData(prev => {
      const next = { ...prev };
      return next;
    });

  }, [isEditMode, initialValues, availableUserGroups]);
  
  useEffect(() => {
    if (!initialSnapshot) return;
    const dirty = !equalShallowCheck(initialSnapshot, formData);
    setIsDirty(dirty);

    if (typeof onDirtyChange === 'function') {
      onDirtyChange(dirty);
    }
  }, [formData, initialSnapshot]);



  const renderAuditValue = (v) => v || '—';

  const handleChangePasswordClick = (e) => {
    e.preventDefault();
    if (onChangePasswordClick) {
      onChangePasswordClick(formData);
    }    
  }

  const selectedGroups = useMemo(() => {
    const ids = new Set((formData.groupIds || []).map(String));
    return (availableUserGroups || []).filter((g) =>
      ids.has(String(g.id))
    );
  }, [formData.groupIds, availableUserGroups]);


  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        py: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 3,
        }}
      >
        <Box component="form" onSubmit={handleSubmitInternal}>
          <Stack spacing={2.5}>
            <Typography fontWeight={600}>
              User Details
            </Typography>

            <Divider />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                error={!!errors.username}
                helperText={errors.username || " "}
                fullWidth
                size="small"
                disabled={isEditMode}
                inputProps={{ minLength: 3, autoComplete: 'off' }}
              />

              <TextField
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                size="small"
              />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email || " "}
                fullWidth
                size="small"
              />

              <TextField
                label="X3 Full Name"
                name="x3fullname"
                value={formData.x3fullname}
                onChange={handleChange}
                disabled={isEditMode}
                fullWidth
                size="small"
              />
            </Stack>

            {!isEditMode && (
              <TextField
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password || " "}
                fullWidth
                size="small"
                inputProps={{ minLength: 12, autoComplete: 'new-password' }}
              />
            )}

            <TextField
              label="Type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              error={!!errors.type}
              helperText={errors.type || " "}
              fullWidth
              size="small"
              select={Array.isArray(types) && types.length > 0}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              disabled={isEditingOwnProfile && !isAdmin}
            >
              <option value="" disabled>Please select</option>
              {Array.isArray(types) &&
                types.map((t) => {
                  const val = typeof t === "string" ? t : t.value || t.name || t.id;
                  const label = typeof t === "string" ? t : t.label || t.name || t.value || t.id;
                  return (
                    <option key={val} value={String(val).toLowerCase()}>
                      {label}
                    </option>
                  );
                })}
            </TextField>

            <Autocomplete
              multiple
              options={availableUserGroups || []}
              getOptionLabel={(opt) => opt?.name || ""}
              value={selectedGroups}
              onChange={handleGroupsChange}
              disabled={isEditingOwnProfile && !isAdmin}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Groups"
                  placeholder="Select groups"
                  size="small"
                />
              )}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={!!formData.isActive}
                  name="isActive"
                  onChange={handleChange}
                  disabled={isEditingOwnProfile && !isAdmin}
                />
              }
              label="Active"
            />

            <Divider />

            {isEditMode && (
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Audit</Typography>
                <Typography variant="body2" color="text.secondary">
                  Created by: <strong>{formData.audit?.createdByName || "—"}</strong>{" "}
                  ({formData.audit?.createdOn ? String(formatDate(formData.audit.createdOn)) : "—"})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Modified by: <strong>{formData.audit?.modifiedByName || "—"}</strong>{" "}
                  ({formData.audit?.modifiedOn ? String(formatDate(formData.audit.modifiedOn)) : "—"})
                </Typography>
              </Stack>
            )}

            <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
              {isEditMode && typeof onChangePasswordClick === "function" && (
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => onChangePasswordClick(formData)}
                  sx={{ textTransform: "none" }}
                >
                  Change password
                </Button>
              )}

              <Button type="submit" variant="contained" sx={{ textTransform: "none", px: 3 }}>
                {isEditMode ? "Save changes" : "Create user"}
              </Button>
            </Stack>

            {/* Optional debug */}
            {/* <pre>{JSON.stringify(formData, null, 2)}</pre> */}
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}

export default UserForm;